<template>
	<div class="price-overview" id="price-overview">
		<!-- Header -->
		<div class="price-overview__header" id="price-overview__header">
			<div class="grid-x grid-margin-x">
				<!-- Period label -->
				<div class="cell small-2">
					<div class="price-overview__cell">
						<span class="price-overview__header-heading">
							{{ $t('priceOverview.period') }}
						</span>
					</div>
				</div>

				<div class="cell small-12 large-10">
					<div class="grid-container">
						<div class="grid-x grid-margin-x">
							<!-- Short week periods -->
							<div class="cell small-6">
								<div class="price-overview__cell border-left">
									<span class="price-overview__header-heading">
										{{ $t('priceOverview.shortWeek') }}
									</span>

									<div class="grid-x grid-margin-x">
										<template v-for="seasonPeriod in season.seasonPeriods">
											<div
												v-if="seasonPeriod.length < 6 && !seasonPeriod.special"
												class="cell small-6"
												:key="seasonPeriod.guid"
											>
												<span class="price-overview__header-subheading">
													{{
														seasonPeriod.startdayname +
														'-' +
														seasonPeriod.enddayname
													}}
												</span>
											</div>
										</template>
									</div>
								</div>
							</div>

							<!-- Full weeks periods -->
							<div class="cell small-6">
								<div class="price-overview__cell border-left">
									<span class="price-overview__header-heading">
										{{ $t('priceOverview.weeks') }}
									</span>

									<div class="grid-x grid-margin-x">
										<template v-for="seasonPeriod in season.seasonPeriods">
											<div
												v-if="seasonPeriod.length >= 6 && !seasonPeriod.special"
												class="cell small-6"
												:key="seasonPeriod.guid"
											>
												<span class="price-overview__header-subheading">
													{{
														seasonPeriod.startdayname +
														'-' +
														seasonPeriod.enddayname
													}}
												</span>
											</div>
										</template>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Week row -->
		<div
			v-for="week in modifiedWeeksList"
			:key="week.weekNumber"
			:class="[
				'price-overview__row',
				week.special ? 'price-overview__row--special' : ''
			]"
		>
			<div class="grid-x grid-margin-x">
				<!-- Week number -->
				<div class="cell small-2 price-overview__row-heading">
					<div class="price-overview__cell">
						<span v-if="!week.special" class="price-overview__week">
							{{ $t('priceOverview.week') }} {{ week.weekNumber }}
						</span>
						<template v-else>
							<span class="price-overview__row-subheading">
								{{ $t('priceOverview.special') }}
							</span>
							<span v-if="!week.special" class="price-overview__row-heading">
								{{ week.weekPeriods[0].pricecodedescription }}
							</span>
							<span v-else class="price-overview__row-heading">
								{{ $t('priceOverview.week') }} {{ week.weekNumber }}
							</span>
						</template>
					</div>
				</div>

				<!-- Standard week -->
				<div v-if="!week.special" class="cell small-12 large-10">
					<div class="grid-container">
						<!-- Weekperiods -->
						<div class="grid-x grid-margin-x" v-if="week.priceFrom === 0">
							<!-- v-if="activePeriods(week.weekPeriods).length > 0" -->

							<template v-for="weekPeriod in week.weekPeriods">
								<!-- Kortvecka -->
								<div
									v-if="weekPeriod.length < 6 && !weekPeriod.special"
									:key="weekPeriod.periodId"
									class="kortvecka cell small-6 large-3"
								>
									<div class="price-overview__cell border-left border-bottom">
										<!-- border-left border-right border-bottom -->

										<!-- Days label -->
										<span class="price-overview__row-period">
											{{
												weekPeriod.startdayname + '-' + weekPeriod.enddayname
											}}
										</span>

										<!-- Available period -->
										<RadioButtonButton
											v-if="weekPeriod.available"
											:id="
												convertDateSpanToId(
													weekPeriod.fromdate,
													weekPeriod.enddate
												)
											"
											name="pricelist"
											type="pricelist"
											:value="
												formatDateSpan(weekPeriod.fromdate, weekPeriod.todate)
											"
											:fromdate="weekPeriod.fromdate"
											:todate="weekPeriod.todate"
											:text="formatMoney(weekPeriod.price) + ' kr'"
											:tooltip="
												formatDateSpanShort(
													weekPeriod.fromdate,
													weekPeriod.todate
												)
											"
											:isChecked="
												weekPeriod.fromdate === startDate &&
												weekPeriod.todate === endDate
											"
											:isDisabled="false"
											:startdayname="weekPeriod.startdayname"
											:enddayname="weekPeriod.enddayname"
											:length="weekPeriod.length"
											@selected="periodSelected"
										/>

										<!-- Not available period -->
										<Button
											v-else
											color="green"
											size="small"
											:full="true"
											:disabled="true"
										>
											{{ $t('priceOverview.notAvailable') }}
										</Button>

										<!-- Date label -->
										<span
											v-if="weekPeriod.available"
											class="price-overview__row-dates"
										>
											{{
												formatDateSpanShort(
													weekPeriod.fromdate,
													weekPeriod.todate
												)
											}}
										</span>

										<!-- Addon -->
										<span
											v-if="weekPeriod.addon"
											class="price-overview__row-addons"
										>
											<span class="star">
												<svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon'" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M6 0l1.413 4.209L12 4.202 8.287 6.798 9.707 11 6 8.398 2.293 11l1.42-4.202L0 4.202l4.587.007L6 0z" fill="#fff"/></svg>
											</span>
											<span class="show-for-medium">
												{{ $t('priceOverview.including') }}
											</span>
											<span class="hide-for-medium">
												{{ $t('priceOverview.incl') }}
											</span>
											{{ weekPeriod.addon }}
										</span>

										<!-- <div v-else :key="weekPeriod.periodId">
												<Button color="green" size="small" :disabled="true">
													Ej tillgänglig
												</Button>
											</div> -->
										<!-- </template> -->
									</div>
								</div>

								<!-- Långvecka -->
								<div
									v-if="weekPeriod.length >= 6 && !weekPeriod.special"
									:key="weekPeriod.periodId"
									class="longvecka cell small-6 large-3"
								>
									<div class="price-overview__cell border-left border-bottom">
										<!-- border-left border-right border-bottom -->

										<!-- Days label -->
										<span class="price-overview__row-period">
											{{
												weekPeriod.startdayname + '-' + weekPeriod.enddayname
											}}
										</span>

										<!-- Available period -->
										<RadioButtonButton
											v-if="weekPeriod.available"
											:id="
												convertDateSpanToId(
													weekPeriod.fromdate,
													weekPeriod.enddate
												)
											"
											name="pricelist"
											type="pricelist"
											:value="
												formatDateSpan(weekPeriod.fromdate, weekPeriod.todate)
											"
											:fromdate="weekPeriod.fromdate"
											:todate="weekPeriod.todate"
											:text="formatMoney(weekPeriod.price) + ' kr'"
											:tooltip="
												formatDateSpanShort(
													weekPeriod.fromdate,
													weekPeriod.todate
												)
											"
											:isChecked="
												weekPeriod.fromdate === startDate &&
												weekPeriod.todate === endDate
											"
											:isDisabled="false"
											:startdayname="weekPeriod.startdayname"
											:enddayname="weekPeriod.enddayname"
											:length="weekPeriod.length"
											@selected="periodSelected"
										/>

										<!-- Not available period -->
										<Button
											v-else
											color="green"
											size="small"
											:full="true"
											:disabled="true"
										>
											{{ $t('priceOverview.notAvailable') }}
										</Button>

										<!-- Date label -->
										<span
											v-if="weekPeriod.available"
											class="price-overview__row-dates"
										>
											{{
												formatDateSpanShort(
													weekPeriod.fromdate,
													weekPeriod.todate
												)
											}}
										</span>

										<!-- Addon -->
										<span
											v-if="weekPeriod.addon"
											class="price-overview__row-addons"
										>
											<span class="star">
												<svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon'" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M6 0l1.413 4.209L12 4.202 8.287 6.798 9.707 11 6 8.398 2.293 11l1.42-4.202L0 4.202l4.587.007L6 0z" fill="#fff"/></svg>
											</span>
											<span class="show-for-medium">
												{{ $t('priceOverview.including') }}
											</span>
											<span class="hide-for-medium">
												{{ $t('priceOverview.incl') }}
											</span>
											{{ weekPeriod.addon }}
										</span>

										<!-- <div v-else :key="weekPeriod.periodId">
													<Button color="green" size="small" :disabled="true">
														Ej tillgänglig
													</Button>
												</div> -->
										<!-- </template> -->
									</div>
								</div>
							</template>
						</div>

						<!-- Book per day CTA (if no weekperiods exists -->
						<div
							v-else
							class="price-overview__cell border-left price-overview__book-per-day"
						>
							{{ $t('priceOverview.from') }}
							<span class="price-overview__bg-green">
								{{ formatMoney(week.priceFrom) }} kr
							</span>
							&nbsp;{{ $t('priceOverview.perDay') }} -
							<button
								type="button"
								class="price-overview__link"
								@click="$emit('calendar-view')"
							>
								{{ $t('priceOverview.bookPerDayCTA') }}
							</button>
						</div>
					</div>
				</div>

				<!-- Special week -->
				<div v-else class="cell small-12 large-10">
					<div class="grid-container">
						<div class="grid-x grid-margin-x">
							<div
								v-for="weekPeriod in activePeriods(week.weekPeriods)"
								:key="weekPeriod.periodId"
								class="cell small-6 large-3"
							>
								<div class="price-overview__cell">
									<!-- Dates label (desktop) -->
									<span class="price-overview__row-period show-for-large">
										{{
											formatDateSpanShort(
												weekPeriod.fromdate,
												weekPeriod.todate
											)
										}}
									</span>

									<!-- Weekdays label (mobile) -->
									<span class="price-overview__row-period hide-for-large">
										{{ weekPeriod.startdayname + '-' + weekPeriod.enddayname }}
									</span>

									<!-- Available period -->
									<RadioButtonButton
										v-if="weekPeriod.available"
										:id="
											convertDateSpanToId(
												weekPeriod.fromdate,
												weekPeriod.todate
											)
										"
										:value="
											formatDateSpan(weekPeriod.fromdate, weekPeriod.todate)
										"
										:fromdate="weekPeriod.fromdate"
										:todate="weekPeriod.todate"
										:text="formatMoney(weekPeriod.price) + ' kr'"
										name="pricelist"
										type="pricelist"
										:tooltip="
											formatDateSpanShort(
												weekPeriod.fromdate,
												weekPeriod.todate
											)
										"
										bg="dark"
										:isChecked="
											weekPeriod.fromdate === startDate &&
											weekPeriod.todate === endDate
										"
										:isDisabled="false"
										@selected="periodSelected"
									/>

									<!-- Not available period -->
									<Button
										v-else
										color="green"
										size="small"
										:full="true"
										bg="dark"
										:disabled="true"
									>
										{{ $t('priceOverview.notAvailable') }}
									</Button>

									<!-- Dates label -->
									<span
										v-if="weekPeriod.available"
										class="price-overview__row-dates"
									>
										{{
											formatDateSpanShort(
												weekPeriod.fromdate,
												weekPeriod.todate
											)
										}}
									</span>

									<!-- Addon -->
									<span
										v-if="weekPeriod.addon"
										class="price-overview__row-addons"
									>
										<span class="star">
											<svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon'" v-bind:svg-inline="''" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path d="M6 0l1.413 4.209L12 4.202 8.287 6.798 9.707 11 6 8.398 2.293 11l1.42-4.202L0 4.202l4.587.007L6 0z" fill="#fff"/></svg>
										</span>
										<span class="show-for-medium">
											{{ $t('priceOverview.including') }}
										</span>
										<span class="hide-for-medium">
											{{ $t('priceOverview.incl') }}
										</span>
										{{ weekPeriod.addon }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Week number -->
			<div class="price-overview__row-label-vertical">
				<span v-if="!week.special" class="price-overview__week">
					{{ $t('priceOverview.week') }} {{ week.weekNumber }}
				</span>
				<template v-else>
					{{ $t('priceOverview.week') }} {{ week.weekNumber }}
				</template>
			</div>
		</div>
	</div>
</template>

<script>
import store from '../../vueX/store'
import mixin from '../../mixin'

import Button from '../atoms/Button'
import RadioButtonButton from '../atoms/RadioButtonButton.vue'

export default {
	name: 'PriceOverview',
	store,
	mixins: [mixin],
	components: {
		Button,
		RadioButtonButton
	},
	props: {
		season: {
			type: Object,
			default: () => {}
		},
		startDate: {
			type: String,
			default: ''
		},
		endDate: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			// priceOverviewStartDate: '',
			// priceOverviewEndDate: ''
		}
	},
	methods: {
		periodSelected(
			id,
			value,
			startdayname,
			enddayname,
			length,
			fromdate,
			todate
		) {
			// this.priceOverviewStartDate = fromdate
			// this.priceOverviewEndDate = todate

			this.$emit(
				'period-selected',
				id,
				value,
				startdayname,
				enddayname,
				length,
				fromdate,
				todate
			)
		},
		standardSeasonPeriods(periodList) {
			return periodList.filter(i => i.special != true)
		},
		activePeriods(periodList) {
			return periodList.filter(i => i.fromdate !== null)
		},
		formatWeekSpan(weekList) {
			if (weekList.length > 1) {
				//Format week numbers: [50,51,52] => '50-52')
				if (typeof weekList[0] == 'string') {
					return weekList[0].slice(0, 2) + '-' + weekList[weekList.length - 1]
				} else {
					return weekList[0] + '-' + weekList[weekList.length - 1]
				}
			} else {
				return weekList[0]
			}
		}
	},
	computed: {
		modifiedWeeksList() {
			function isNotAvailable(period) {
				return period.available === false
			}

			/*
				The purpose of this method is to: 
				Combine weeks with no periods into one row in the price overview ("w41-45")
				We also format the week numbers into a span
				and calculate the lowest fromPrice within the span.
			*/

			let newList = [...this.season.weeks]
			let weekSpanDaysStarted = false
			let weekSpanUnavailableStarted = false
			let combinedWeeksIndex = -1
			let combinedWeeksFromPrice = 0
			let combinedWeeksWeekNumbers = []

			newList = newList.filter((week, index) => {
				// Weeks without periods (= only price-per-day is available)
				if (week.priceFrom != 0 && !week.special) {
					//Continue on started week-span
					if (weekSpanDaysStarted === true) {
						//Save lowest from price
						if (week.priceFrom < combinedWeeksFromPrice) {
							combinedWeeksFromPrice = week.priceFrom
						}
						//Save week number
						combinedWeeksWeekNumbers.push(week.weekNumber)
					} else {
						//Make some adjustments to previous week-span(unavailable)
						if (weekSpanUnavailableStarted === true) {
							//Format week numbers (51,51,51 => 51-53)
							combinedWeeksWeekNumbers = this.formatWeekSpan(
								combinedWeeksWeekNumbers
							)

							//Set week numbers
							newList[combinedWeeksIndex].weekNumber = combinedWeeksWeekNumbers
						}

						//Create beginning of new week-span

						//Reset all week-span data
						combinedWeeksFromPrice = 0
						combinedWeeksWeekNumbers = []
						weekSpanDaysStarted = false
						weekSpanUnavailableStarted = false
						combinedWeeksIndex = -1

						//Save index, week number and priceFrom
						weekSpanDaysStarted = true
						combinedWeeksIndex = index
						combinedWeeksFromPrice = week.priceFrom
						combinedWeeksWeekNumbers.push(week.weekNumber)

						return week
					}

					//Weeks with periods
				} else {
					// Fully booked week
					if (!week.special && week.weekPeriods.every(isNotAvailable)) {
						//Continue on started week-span(unavailable)
						if (weekSpanUnavailableStarted === true) {
							//Save week number
							combinedWeeksWeekNumbers.push(week.weekNumber)
						} else {
							//Make some adjustments to previous week-span(days)
							if (weekSpanDaysStarted === true) {
								//Set lowest from price
								newList[combinedWeeksIndex].priceFrom = combinedWeeksFromPrice

								//Format week numbers (51,51,51 => 51-53)
								combinedWeeksWeekNumbers = this.formatWeekSpan(
									combinedWeeksWeekNumbers
								)

								//Set week numbers
								newList[
									combinedWeeksIndex
								].weekNumber = combinedWeeksWeekNumbers
							}

							//Create beginning of new week-span(unavailable)
							//Reset all week-span data
							combinedWeeksFromPrice = 0
							combinedWeeksWeekNumbers = []
							weekSpanDaysStarted = false
							weekSpanUnavailableStarted = false
							combinedWeeksIndex = -1

							//Save index and week number
							weekSpanUnavailableStarted = true
							combinedWeeksIndex = index
							combinedWeeksWeekNumbers.push(week.weekNumber)

							return week
						}

						// Weeks with available periods
					} else {
						//Make some adjustments to previous week-span
						if (combinedWeeksIndex != -1) {
							//Set lowest from price
							newList[combinedWeeksIndex].priceFrom = combinedWeeksFromPrice
							//Format week numbers (51,51,51 => 51-53)
							combinedWeeksWeekNumbers = this.formatWeekSpan(
								combinedWeeksWeekNumbers
							)
							//Set week numbers
							newList[combinedWeeksIndex].weekNumber = combinedWeeksWeekNumbers
						}

						//Reset all week-span data
						combinedWeeksFromPrice = 0
						combinedWeeksWeekNumbers = []
						weekSpanDaysStarted = false
						weekSpanUnavailableStarted = false
						combinedWeeksIndex = -1

						return week
					}
				}
			})

			return newList
		}
	}
}
</script>

<style scoped lang="scss">
.price-overview {
	overflow: hidden;

	&__header,
	&__row {
		padding: 0 0 0 30px;
	}

	&__header {
		display: none;

		&-heading {
			display: block;
			font-size: 18px;
			text-align: center;
			padding: 0 15px;
		}

		&-subheading {
			display: block;
			font-size: 14px;
			text-align: center;
			color: #546482;
		}

		.price-overview__header-heading:first-child {
			&:last-child {
				text-align: left;
			}
		}
	}

	&__row {
		position: relative;
		// overflow: visible;

		&-label-vertical {
			position: absolute;
			bottom: calc(50% - 7px);
			left: 0;
			width: 120px;
			height: 14px;
			text-align: center;
			margin-left: -35px;
			transform-origin: center;
			transform: rotate(270deg);
			line-height: 1;
		}

		> .grid-x {
			// align-items: center;
		}

		&-period {
			display: inline-block;
			width: 100%;
			text-align: center;
			font-size: 12px;
			font-weight: 400;
			margin-bottom: 6px;
		}

		&-addons {
			text-align: center;
			display: block;
			margin: 3px 0 0 0;
			font-size: 10px;

			.star {
				width: 13px;
				height: 13px;
				display: inline-block;
				vertical-align: middle;
				border-radius: 10px;
				margin-right: 4px;
				background: #a37ad9;
				.icon {
					width: 9px;
				}
			}

			&:before {
				// content: '';
				// width: 13px;
				// height: 13px;
				// display: inline-block;
				// vertical-align: middle;
				// background: #a37ad9 url(../../assets/icons/star.svg) 2px 2px no-repeat;
				// border-radius: 10px;
				// margin-right: 5px;
				// background-size: 9px;
			}
		}

		&-heading {
			display: none;

			.price-overview__cell {
				text-align: left;
			}
		}

		&-subheading {
			display: none;
			font-size: 14px;
			font-weight: 600;
		}

		&-label {
			display: block;
			font-size: 14px;
		}

		&-dates {
			text-align: center;
			display: block;
			font-size: 12px;
			margin: -3px 0 0 0;
		}
		button + .price-overview__row-dates {
			margin-top: 3px;
		}

		&:nth-child(even) {
			background: #f4f5f7;
			border-top: 1px solid #000;
			border-bottom: 1px solid #000;
		}

		&--special {
			background: $colorPrimary8 !important;
			color: $colorWhite;
			position: relative;

			+ .price-overview__row--special {
				border-top: 1px solid $colorWhite;
			}

			.price-overview__cell {
				text-align: center;
			}

			.price-overview__row-heading {
				font-weight: 600;
			}

			.price-overview__row-subheading {
				font-weight: 400;
			}

			span.price-overview__row-heading {
				padding-left: 15px;
			}

			span.price-overview__row-subheading {
				padding-left: 15px;
			}
		}
	}

	&__cell {
		text-align: center;
		padding: 15px 0;
	}

	&__link {
		border: none;
		background: none;
		font-size: 16px;
		color: #04befb;
		line-height: 1;
		cursor: pointer;

		&:hover {
			color: #000;
			text-decoration: underline;
		}
	}

	&__book-per-day {
		font-size: 16px;
		text-align: center;
		padding: 30px 0;
	}

	&__bg-green {
		background: $colorGreen;
		padding: 0 8px;
		border-radius: 3px;
		color: $colorWhite;
		font-weight: 600;
	}

	.border-bottom {
		height: 100%;
		position: relative;
		box-sizing: border-box;
		margin-bottom: 25px;

		&:after {
			content: '';
			position: absolute;
			width: calc(100% + 40px);
			height: 1px;
			border-bottom: 1px dashed #d4d8df;
			left: 0;
			bottom: 10px;
		}
	}
	.kortvecka,
	.longvecka {
		&:nth-last-child(-n + 2) .border-bottom {
			margin-bottom: 0;
			&:after {
				display: none;
			}
		}
	}

	@media only screen and (min-width: 64em) {
		//min-width: 980px;

		&__header,
		&__row {
			// padding: 15px 20px;
			padding: 0;
			// overflow: hidden;
		}
		&__cell {
			padding: 15px 0;
			text-align: center;
		}

		&__header {
			display: block;
		}

		&__row {
			&-label-vertical,
			&-period {
				display: none;
			}
			&-heading {
				display: block;
			}
			&-subheading {
				display: block;
			}

			button {
				min-width: 160px;
			}

			&-addons {
				font-size: 13px;

				.star {
					width: 20px;
					height: 20px;
					margin-right: 4px;

					.icon {
						width: 12px;
					}
				}
			}

			&-dates {
				display: none;
			}

			&:nth-child(even) {
				border-top: none;
				border-bottom: none;
			}

			&--special {
				.price-overview__row-period {
					display: inline-block;
					text-align: left;
				}

				.price-overview__cell {
					text-align: left;
				}

				.cell.small-6.large-3:nth-child(4n + 1) .price-overview__cell {
					// border-left: 1px solid $colorWhite;
				}
				.cell.small-6.large-3:nth-child(4n + 1) .price-overview__cell {
					position: relative;
					&:before {
						content: '';
						position: absolute;
						height: 100%;
						width: 1px;
						border-left: 1px solid #ffffff;
						left: -20px;
						top: 0;
					}
				}
			}
		}

		&__week {
			padding: 8px 0 8px 15px;
			display: inline-block;
		}

		&__book-per-day {
			padding-top: 23px;
		}

		.border-bottom {
			margin-bottom: 0;

			&:after {
				display: none;
				left: auto;
				bottom: auto;
				border-bottom: none;
			}
		}

		.border-left {
			height: 100%;
			position: relative;
			box-sizing: border-box;
			border: none !important;

			&:before {
				content: '';
				position: absolute;
				height: 100%;
				width: 1px;
				border-left: 1px solid #d4d8df;
				left: -20px;
				top: 0;
			}
		}
		.kortvecka,
		.longvecka {
			&:nth-child(even) {
				.border-left:before {
					// border-style: dashed;
					border-left: 1px dotted #d4d8df;
				}
			}
		}

		.border-right {
			height: 110%;
			position: relative;
			box-sizing: border-box;

			&:after {
				display: block;
				content: '';
				position: absolute;
				height: 100%;
				width: 1px;
				border-left: 1px dashed #d4d8df;
				right: -20px;
				top: 0;
			}
		}

		&--sticky {
			position: relative;
			padding-top: 95px;

			.price-overview__header {
				max-width: calc(1200px - 77px); //60px margin/padding and 17px scrollbar
				width: calc(100% - 137px); //120px margin/padding and 17px scrollbar
				position: fixed;
				top: 12.5vh;
				z-index: 99999;
				background: #fff;
				transition: all 0.3s ease;
			}
		}
	}
}

@media only screen and (max-width: 1023px) {
	.price-overview .border-bottom:after,
	.price-overview .border-bottom,
	.price-overview .border-left {
		border: none!important;
	}

	.price-overview .border-bottom {
		margin-bottom: 0!important;
	}

	.price-overview .btn.btn--green.disabled {
		font-size: 12px;
	}
}
</style>
